// Edit surface for a doctor's own listing — deliberately reuses the exact
// header / sticky-tabs / section layout and classes from providers.$id.tsx
// (the public profile page) so editing feels like editing the real page in
// place, not a bolted-on form. Kept as its own route rather than an edit-mode
// branch inside providers.$id.tsx: that file is the SEO-critical public page
// (canonical URLs, structured data, 6.3M+ listings) and threading editable
// state through its ~1800 lines isn't worth the regression risk for a
// same-visual-result feature only the signed-in owner ever sees.
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PhotoUploader } from "@/components/PhotoUploader";
import { SpecialtySelect } from "@/components/forms/SpecialtySelect";
import { AddressAutocomplete } from "@/components/forms/AddressAutocomplete";
import { CityZipInput } from "@/components/forms/CityZipInput";
import { MultiTagSelect } from "@/components/forms/MultiTagSelect";
import { AIDescriptionField } from "@/components/forms/AIDescriptionField";
import { AvailabilityEditor } from "@/components/ai/AvailabilityEditor";
import { GoogleCalendarButton } from "@/components/calendar/GoogleCalendarButton";
import { INSURANCES, SERVICES, LANGUAGES, type Provider } from "@/data/heallexa";
import { loadMyListingsFn, updateMyListingFn } from "./-dashboard.doctor.functions";
import {
  ShieldCheck, CheckCircle2, MapPin, Loader2, Navigation, ExternalLink, Eye,
} from "lucide-react";

export const Route = createFileRoute("/dashboard/doctor_/edit/$id")({
  component: DoctorProfileEdit,
});

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "services", label: "Services" },
  { key: "location", label: "Location" },
] as const;
type TabKey = typeof TABS[number]["key"];

function DoctorProfileEdit() {
  const { id } = useParams({ from: "/dashboard/doctor_/edit/$id" });
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate({ to: "/login" }); return; }
    (async () => {
      const mine = ((await loadMyListingsFn()) ?? []) as Provider[];
      const match = mine.find((p) => p.id === id);
      if (!match) { setNotFound(true); return; }
      setProvider(match);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, id]);

  useEffect(() => {
    if (notFound) navigate({ to: "/dashboard/doctor", search: { tab: "listings" } as any, replace: true });
  }, [notFound, navigate]);

  if (loading || (!provider && !notFound)) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }
  if (!provider) return null;

  return <EditorBody provider={provider} />;
}

function EditorBody({ provider }: { provider: Provider }) {
  const navigate = useNavigate();
  const save = useServerFn(updateMyListingFn);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState<TabKey>("overview");
  const tabsRef = useRef<HTMLDivElement>(null);
  const suppressSpy = useRef(false);
  const suppressSpyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const [data, setData] = useState({
    practice: provider.practice,
    specialty: provider.specialty,
    phone: provider.phone,
    website: provider.website ?? "",
    hours: provider.hours ?? "",
    about: provider.about ?? "",
    short_description: (provider as any).short_description ?? "",
    address: provider.address,
    city: provider.city,
    state: provider.state ?? "",
    insurance: provider.insurance,
    languages: provider.languages,
    services: provider.services,
    logo_url: provider.logo_url ?? "",
    available_days: ((provider as any).available_days ?? []) as string[],
  });
  const [photoUrl, setPhotoUrl] = useState<string | null>(
    provider.display_photo_url ?? provider.profile_photo_url ?? null,
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (suppressSpy.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id as TabKey);
      },
      { rootMargin: "-140px 0px -55% 0px", threshold: [0.1, 0.5, 1] },
    );
    TABS.forEach((t) => {
      const el = document.getElementById(t.key);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    setActive(id as TabKey);
    suppressSpy.current = true;
    clearTimeout(suppressSpyTimer.current);
    suppressSpyTimer.current = setTimeout(() => { suppressSpy.current = false; }, 900);
    const y = el.getBoundingClientRect().top + window.scrollY - 140;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await save({
        data: {
          id: provider.id,
          payload: {
            practice: data.practice,
            specialty: data.specialty,
            phone: data.phone,
            website: data.website,
            hours: data.hours,
            about: data.about,
            short_description: data.short_description,
            address: data.address,
            city: data.city,
            state: data.state,
            insurance: data.insurance,
            languages: data.languages,
            services: data.services,
            logo_url: data.logo_url,
            available_days: data.available_days,
          },
        },
      });
      toast.success("Profile updated");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to update listing");
    } finally {
      setSaving(false);
    }
  };

  const identityVerified = !!(provider as any).verified_badge;
  const verified = provider.listing_status !== "unclaimed";
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(provider.address)}`;
  const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(provider.address)}&output=embed`;

  return (
    <div className="bg-background pb-1">
      {/* EDIT-MODE BAR — dashboard sidebar already provides "back"; this just
          links out to the real page, same slot the public page's breadcrumbs sit in. */}
      <nav className="pt-6 pb-2 border-b border-border/60 bg-background">
        <div className="mx-auto max-w-[1200px] px-6 flex flex-wrap items-center justify-end gap-2">
          <Link to="/providers/$id" params={{ id: provider.id }} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary">
            <Eye className="h-3.5 w-3.5" /> View live profile <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </nav>

      {/* HEADER — same grid/photo/badge treatment as the public profile */}
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-[1200px] grid grid-cols-1 md:grid-cols-[280px_1fr_auto] gap-8 items-start md:px-6 md:pb-10 md:pt-10">
          <div className="px-6 pt-6 md:px-0 md:pt-0">
            <PhotoUploader
              providerId={provider.id}
              initialUrl={photoUrl}
              onUploaded={(url) => setPhotoUrl(url)}
              boxClassName="max-w-none md:max-w-[280px]"
            />
          </div>

          <div className="min-w-0 px-6 pt-2 md:px-0 md:pt-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              {identityVerified ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold border border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30 px-2 py-0.5 rounded-md">
                  <ShieldCheck className="h-3 w-3" /> Identity Verified
                </span>
              ) : verified ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-primary font-semibold border border-primary/40 px-2 py-0.5 rounded-md">
                  <CheckCircle2 className="h-3 w-3" /> Verified
                </span>
              ) : (
                <span className="text-[10px] text-muted-foreground border border-border px-2 py-0.5 rounded-md">Unclaimed</span>
              )}
            </div>

            <div className="max-w-[280px]">
              <SpecialtySelect value={data.specialty} onChange={(v) => setData({ ...data, specialty: v })} />
            </div>

            <Input
              value={data.practice}
              onChange={(e) => setData({ ...data, practice: e.target.value })}
              placeholder="Practice name"
              className="mt-3 h-auto border-0 border-b border-dashed border-border/70 rounded-none px-0 font-display text-[clamp(1.6rem,3.4vw,2.6rem)] font-medium tracking-[-0.03em] leading-[1.1] text-foreground bg-transparent focus-visible:ring-0 focus-visible:border-primary"
            />

            <div className="mt-3 flex items-start gap-2 text-[13px] text-muted-foreground">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground/70" />
              <span>{[data.address, data.city, data.state].filter(Boolean).join(", ")}</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Edit your address in Contact information below.</p>
          </div>

          <div className="flex flex-col gap-2 w-full md:w-[220px] px-6 md:px-0 pb-6 md:pb-0 md:self-start md:mt-1.5">
            <Button onClick={doSave} disabled={saving} className="h-11 rounded-md">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
            </Button>
            <Button variant="outline" asChild className="h-11 rounded-md">
              <Link to="/dashboard/doctor" search={{ tab: "listings" } as any}>Cancel</Link>
            </Button>
            <GoogleCalendarButton className="h-11 rounded-md w-full" />
          </div>
        </div>
      </header>

      {/* STICKY TABS — identical gradient-underline treatment to the public page */}
      <div className="sticky top-[56px] z-30 bg-background border-b border-border shadow-[0_1px_0_rgba(11,79,138,0.08),0_4px_16px_-8px_rgba(11,79,138,0.18)]">
        <div ref={tabsRef} aria-label="Profile sections" className="mx-auto max-w-[1200px] flex overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {TABS.map((t, index) => (
            <button
              key={t.key}
              onClick={() => scrollToSection(t.key)}
              className={`flex-1 group relative h-16 sm:h-20 px-3 text-[13px] sm:text-[15px] tracking-[0.01em] transition-all duration-300 flex items-center justify-center text-center ${
                active === t.key
                  ? "text-primary font-semibold bg-gradient-to-b from-primary/[0.04] to-transparent"
                  : "text-foreground/65 font-medium hover:text-primary hover:bg-primary/[0.025]"
              } ${index !== 0 ? "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-8 before:w-px before:bg-border/60" : ""}`}
            >
              <span className="leading-tight relative">
                {t.label}
                <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 h-[2px] rounded-full bg-gradient-to-r from-primary via-[#1976d2] to-primary transition-all duration-300 w-full ${active === t.key ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
              </span>
            </button>
          ))}
        </div>
      </div>

      <main className="mx-auto max-w-[1200px] px-6 py-12">
        <div className="max-w-[760px] space-y-20">
          <div id="overview" className="scroll-mt-32 space-y-20">
            <EditBlock title="Availability">
              <AvailabilityEditor
                providerId={provider.id}
                providerLabel={data.practice || provider.practice}
                plan={provider.plan ?? "free"}
                onUpgrade={() => navigate({ to: "/dashboard/doctor", search: { tab: "billing" } as any })}
              />
              <p className="text-[11px] text-muted-foreground mt-2">Set the days and hours you see patients — this is what powers your public booking calendar.</p>
            </EditBlock>

            <EditBlock title="About">
              <div className="space-y-4">
                <AIDescriptionField
                  label="Short description (tagline)"
                  mode="short"
                  value={data.short_description}
                  onChange={(v) => setData({ ...data, short_description: v })}
                  context={{ practice: data.practice, specialty: data.specialty, city: data.city }}
                />
                <AIDescriptionField
                  label="About"
                  mode="about"
                  rows={5}
                  value={data.about}
                  onChange={(v) => setData({ ...data, about: v })}
                  context={{ practice: data.practice, specialty: data.specialty, city: data.city }}
                />
              </div>
            </EditBlock>

            <EditBlock title="Contact information">
              <div className="space-y-3 rounded-md border border-border bg-card p-4">
                <FieldRow label="Phone"><Input value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} /></FieldRow>
                <FieldRow label="Website"><Input value={data.website} onChange={(e) => setData({ ...data, website: e.target.value })} placeholder="https://" /></FieldRow>
                <FieldRow label="Address">
                  <AddressAutocomplete
                    address={data.address}
                    onPick={({ address, city, state, zip }) =>
                      setData((d: any) => ({ ...d, address, city: city ?? d.city, state: state ?? d.state, ...(zip !== undefined ? { zip } : {}) }))
                    }
                  />
                </FieldRow>
                <FieldRow label="City / State / ZIP">
                  <CityZipInput
                    city={data.city ?? ""}
                    state={(data as any).state ?? ""}
                    zip={(data as any).zip ?? ""}
                    onChange={({ city, state, zip }: { city: string; state: string; zip?: string }) =>
                      setData({ ...data, city, state, ...(zip !== undefined ? { zip } : {}) } as any)
                    }
                  />
                </FieldRow>
              </div>
            </EditBlock>

            <EditBlock title="Languages spoken">
              <MultiTagSelect value={data.languages} onChange={(v) => setData({ ...data, languages: v })} options={LANGUAGES} placeholder="Select languages…" />
            </EditBlock>

            <EditBlock title="Insurance accepted">
              <MultiTagSelect value={data.insurance} onChange={(v) => setData({ ...data, insurance: v })} options={INSURANCES.map((i) => i.id)} placeholder="Select insurance…" />
            </EditBlock>

            <EditBlock title="Office hours">
              <Textarea
                value={data.hours}
                onChange={(e) => setData({ ...data, hours: e.target.value })}
                placeholder={"Mon-Fri 9am-5pm\nSat 10am-2pm"}
                rows={4}
              />
              <p className="text-[11px] text-muted-foreground mt-1.5">One line per day/range — shown to patients exactly as written.</p>
            </EditBlock>
          </div>

          <div id="services" className="scroll-mt-32">
            <EditBlock title="Services offered">
              <MultiTagSelect value={data.services} onChange={(v) => setData({ ...data, services: v })} options={SERVICES} placeholder="Select services…" />
            </EditBlock>
          </div>

          <div id="location" className="scroll-mt-32">
            <EditBlock title="Office location">
              <p className="text-sm text-muted-foreground mb-3 flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {provider.address}</p>
              <div className="aspect-[16/9] w-full rounded-md border border-border bg-muted overflow-hidden mb-4">
                <iframe src={mapEmbed} title={`Map of ${provider.practice}`} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
              <p className="text-[11px] text-muted-foreground mb-3">Reflects your last saved address — save changes above to update the map.</p>
              <Button variant="outline" asChild className="rounded-md">
                <a href={directionsUrl} target="_blank" rel="noreferrer" className="!flex items-center justify-center gap-2">
                  <Navigation className="h-4 w-4" /> Get directions
                </a>
              </Button>
            </EditBlock>
          </div>
        </div>
      </main>

      <div className="sticky bottom-0 z-20 border-t border-border bg-background/95 backdrop-blur-sm">
        <div className="mx-auto max-w-[1200px] px-6 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" asChild>
            <Link to="/dashboard/doctor" search={{ tab: "listings" } as any}>Cancel</Link>
          </Button>
          <Button onClick={doSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="scroll-mt-32">
      <div className="flex items-center justify-between gap-3 mb-5 pb-3 border-b border-border">
        <h2 className="text-[clamp(1.4rem,2.2vw,1.75rem)] font-medium tracking-[-0.02em]">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">{label}</Label>
      {children}
    </div>
  );
}
